import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Params, ResolveFn, Routes } from '@angular/router';
import { FrameworkPrototypeStore } from '../frameworks/framework-prototype.store';

/** Params from the root down — the framework routes split them across nested routes. */
const allParams = (route: ActivatedRouteSnapshot): Params =>
  Object.assign({}, ...route.pathFromRoot.map((r) => r.params));

/** Breadcrumb labels for the framework workspace routes. */
const frameworkName: ResolveFn<string> = (route) =>
  inject(FrameworkPrototypeStore).framework(allParams(route)['key'])?.title ?? 'Framework';

const processName: ResolveFn<string> = (route) => {
  const p = allParams(route);
  return inject(FrameworkPrototypeStore).process(p['key'], p['process'])?.name ?? 'Process';
};

const indicatorName: ResolveFn<string> = (route) => {
  const p = allParams(route);
  return (
    inject(FrameworkPrototypeStore).indicator(p['key'], p['process'], p['indicatorId'])?.name ??
    'Indicator'
  );
};

const projectName: ResolveFn<string> = (route) =>
  inject(FrameworkPrototypeStore).project(allParams(route)['projectId'])?.shortName ?? 'Project';

const interventionName: ResolveFn<string> = (route) => {
  const p = allParams(route);
  return (
    inject(FrameworkPrototypeStore).intervention(p['key'], p['process'], p['interventionId'])
      ?.project ?? 'Intervention'
  );
};

const targetName: ResolveFn<string> = (route) => {
  const p = allParams(route);
  return (
    inject(FrameworkPrototypeStore).target(p['key'], p['process'], p['indicatorId'], p['targetId'])
      ?.name ?? 'Target'
  );
};

export const nationalModuleRoutes: Routes = [
  { path: '', redirectTo: 'overview', pathMatch: 'full', data: { isNotAMenuItem: true } },
  {
    path: 'overview',
    loadComponent: () =>
      import('../dashboard/national-dashboard').then((m) => m.NationalDashboard),
    data: { name: 'Dashboard', icon: 'dashboard', iconType: 'MAT' },
  },
  {
    // The earlier ministry portfolio dashboard, kept off the menu while frameworks are prototyped
    path: 'portfolio',
    loadComponent: () =>
      import('../overview/national-overview').then((m) => m.NationalOverview),
    data: { name: 'Portfolio Overview', isNotAMenuItem: true },
  },
  {
    path: 'ministries',
    loadComponent: () =>
      import('../ministries/ministry-portfolio').then((m) => m.MinistryPortfolio),
    data: { name: 'Ministries', icon: 'apartment', iconType: 'MAT', isNotAMenuItem: true },
  },
  {
    path: 'ministries/:id',
    loadComponent: () =>
      import('../ministries/ministry-detail/ministry-detail').then((m) => m.MinistryDetail),
    data: { name: 'Ministry', isNotAMenuItem: true },
  },
  {
    path: 'ministries/:id/organogram',
    loadComponent: () =>
      import('../ministries/ministry-organogram/ministry-organogram').then(
        (m) => m.MinistryOrganogram,
      ),
    data: { name: 'Organogram', isNotAMenuItem: true },
  },
  {
    path: 'ministries/:id/votes/:voteId',
    loadComponent: () =>
      import('../ministries/vote-detail/vote-detail').then((m) => m.VoteDetail),
    data: { name: 'Institution', isNotAMenuItem: true },
  },
  {
    path: 'frameworks',
    loadComponent: () =>
      import('../frameworks/national-frameworks').then((m) => m.NationalFrameworks),
    data: { name: 'Frameworks', icon: 'account_tree', iconType: 'MAT' },
  },
  {
    path: 'frameworks/sdg',
    loadComponent: () =>
      import('../frameworks/sdg-tracker/sdg-tracker').then((m) => m.SdgTracker),
    data: { name: 'SDG Tracker', isNotAMenuItem: true },
  },
  {
    path: 'frameworks/sdg/goals/:number',
    loadComponent: () =>
      import('../frameworks/sdg-tracker/goal-detail/goal-detail').then((m) => m.SdgGoalDetail),
    data: { name: 'Goal', isNotAMenuItem: true },
  },
  {
    path: 'frameworks/sdg/scorecard',
    loadComponent: () =>
      import('../frameworks/sdg-tracker/scorecard/scorecard').then((m) => m.SdgScorecard),
    data: { name: 'Scorecard', isNotAMenuItem: true },
  },
  {
    path: 'frameworks/sdg/explorer',
    loadComponent: () =>
      import('../frameworks/sdg-tracker/data-explorer/data-explorer').then((m) => m.SdgDataExplorer),
    data: { name: 'Data Explorer', isNotAMenuItem: true },
  },
  {
    path: 'frameworks/accountability',
    loadComponent: () =>
      import('../frameworks/alignment/goal-alignment').then((m) => m.GoalAlignment),
    data: { name: 'Accountability', isNotAMenuItem: true },
  },
  {
    // After the static frameworks/* routes so 'sdg' and 'accountability' still match first
    path: 'frameworks/:key',
    loadComponent: () =>
      import('../frameworks/workspace/framework-workspace').then((m) => m.FrameworkWorkspace),
    data: { isNotAMenuItem: true },
    resolve: { name: frameworkName },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: ':process',
        resolve: { name: processName },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('../frameworks/workspace/framework-process').then(
                (m) => m.FrameworkProcessView,
              ),
          },
          {
            path: 'projects/:projectId',
            loadComponent: () =>
              import('../frameworks/workspace/project-detail').then((m) => m.ProjectDetail),
            resolve: { name: projectName },
          },
          {
            path: 'interventions/:interventionId',
            loadComponent: () =>
              import('../frameworks/workspace/intervention-reports').then(
                (m) => m.InterventionReports,
              ),
            resolve: { name: interventionName },
          },
          {
            path: 'indicators/:indicatorId',
            resolve: { name: indicatorName },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('../frameworks/workspace/indicator-targets').then(
                    (m) => m.IndicatorTargets,
                  ),
              },
              {
                path: 'targets/:targetId',
                loadComponent: () =>
                  import('../frameworks/workspace/target-implementations').then(
                    (m) => m.TargetImplementations,
                  ),
                resolve: { name: targetName },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'planning-cycle',
    loadComponent: () =>
      import('../planning-cycle/planning-cycle').then((m) => m.PlanningCyclePage),
    data: { name: 'Planning & Budget', icon: 'event_available', iconType: 'MAT', isNotAMenuItem: true },
  },
  {
    path: 'submissions',
    loadComponent: () =>
      import('../submissions/submission-queue').then((m) => m.SubmissionQueue),
    data: { name: 'Submissions', icon: 'inbox', iconType: 'MAT', isNotAMenuItem: true },
  },
  {
    path: 'submissions/:id',
    loadComponent: () =>
      import('../submissions/review-submission/review-submission').then((m) => m.ReviewSubmission),
    data: { name: 'Review', isNotAMenuItem: true },
  },
  {
    path: 'consolidation',
    loadComponent: () =>
      import('../consolidation/consolidation').then((m) => m.ConsolidationPage),
    data: { name: 'Consolidation', icon: 'account_balance_wallet', iconType: 'MAT', isNotAMenuItem: true },
  },
  {
    path: 'performance',
    loadComponent: () =>
      import('../performance/performance').then((m) => m.PerformancePage),
    data: { name: 'Performance', icon: 'insights', iconType: 'MAT', isNotAMenuItem: true },
  },
  {
    path: 'reporting',
    loadComponent: () =>
      import('../reporting/national-reporting').then((m) => m.NationalReporting),
    data: { name: 'Reporting', icon: 'summarize', iconType: 'MAT', isNotAMenuItem: true },
  },
  {
    path: 'reporting/quarterly-return',
    loadComponent: () =>
      import('../reporting/quarterly-return/quarterly-return').then((m) => m.QuarterlyReturn),
    data: { name: 'Quarterly Return', isNotAMenuItem: true },
  },
];
